import { useRef, useMemo, useEffect } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { cn } from '@/lib/utils/cn'

export interface VortexProps {
  className?: string
  discCount?: number
  particleCount?: number
  particleColor?: string
  discColor?: string
  discLineWidth?: number
  particleSize?: number
  speed?: number
  rotationDirection?: number
  opacity?: number
  enableCursorInteraction?: boolean
  cursorInfluence?: number
  scale?: number
  radius?: number
}

function parseColor(colorStr: string): THREE.Color {
  if (colorStr.startsWith('rgba') || colorStr.startsWith('rgb')) {
    const match = colorStr.match(/[\d.]+/g)
    if (match && match.length >= 3) return new THREE.Color(parseInt(match[0] as string) / 255, parseInt(match[1] as string) / 255, parseInt(match[2] as string) / 255)
  }
  return new THREE.Color(colorStr)
}

function parseOpacity(colorStr: string): number {
  if (colorStr.startsWith('rgba')) {
    const match = colorStr.match(/[\d.]+/g)
    if (match && match.length >= 4) return parseFloat(match[3] as string)
  }
  return 1.0
}

function getC(t: number, xSweep: number = 30) {
  const clampedT = Math.max(0, Math.min(1.0, t))
  const y = -clampedT * 200
  const t_s = 3 * clampedT * clampedT - 2 * clampedT * clampedT * clampedT
  // Dynamic 3D depth curve for the whirlwind
  const z = 60 * t_s
  const x = xSweep * Math.sin(clampedT * Math.PI)
  return new THREE.Vector3(x, y, z)
}

function Rings({ count = 50, speed = 1, radius = 5, color = '#ffffff', opacity = 0.15, lineWidth = 1, rotationDirection = 1, globalOpacity = 1 }: any) {
  const linesRef = useRef<THREE.Group>(null)
  const threeColor = useMemo(() => parseColor(color), [color])

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry()
    const points = []
    for (let i = 0; i <= 128; i++) {
      const theta = (i / 128) * Math.PI * 2
      points.push(new THREE.Vector3(Math.cos(theta), 0, Math.sin(theta)))
    }
    geo.setFromPoints(points)
    return geo
  }, [])

  const material = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color: threeColor,
      transparent: true,
      opacity: opacity * globalOpacity,
      linewidth: lineWidth,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    })
  }, [threeColor, opacity, globalOpacity, lineWidth])

  const ringsData = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => ({
      u: i / count,
      spinRate: (Math.random() - 0.5) * 0.5 * rotationDirection,
      currentRotation: Math.random() * Math.PI * 2,
      lineObj: new THREE.Line(geometry, material.clone())
    }))
  }, [count, rotationDirection, geometry, material])

  useFrame((_, delta) => {
    if (!linesRef.current) return
    if (typeof document !== 'undefined' && document.hidden) return
    const clampedDelta = (typeof delta === 'number' && !isNaN(delta) && isFinite(delta))
      ? Math.min(Math.max(0, delta), 0.05)
      : 0.016
    const xSweep = 30
    const children = linesRef.current.children
    children.forEach((child, i) => {
      const rData = ringsData[i]!
      rData.u = (rData.u + speed * clampedDelta * 0.03) % 1.0
      if (rData.u < 0) rData.u += 1.0

      const u = rData.u
      const t = Math.max(0, Math.min(0.9999, u)) // linear mapping to see the whole throat evenly

      const pos = getC(t, xSweep)

      child.position.set(pos.x, pos.y, pos.z)

      const baseRotSpeed = (0.2 + Math.pow(t, 2) * 3.0) * rotationDirection
      if (isNaN(rData.currentRotation)) rData.currentRotation = 0
      rData.currentRotation = (rData.currentRotation + (baseRotSpeed + rData.spinRate) * clampedDelta) % (Math.PI * 2)
      child.rotation.y = rData.currentRotation

      // Radius contraction: huge mouth, distinctly wide throat (tornado whirlwind structure)
      const currentRadius = 15 + (radius - 15) * Math.pow(1.0 - t, 2.3)
      child.scale.set(currentRadius, 1, currentRadius)

      const mat = (child as THREE.Line).material as THREE.LineBasicMaterial
      // Theme colors: vibrant emerald green (#43C995) smoothly transitioning into mint and teal along depth
      const hue = (0.41 + t * 0.14) % 1.0
      mat.color.setHSL(hue, 0.9, 0.52)

      // Boost visibility while preserving the fade
      let currentOpacity = 0.55
      if (t < 0.03) currentOpacity = 0.55 * (t / 0.03)
      else if (t > 0.95) currentOpacity = 0.55 * ((1.0 - t) / 0.05)
      mat.opacity = Math.min(currentOpacity * globalOpacity, 0.85)
    })
  })

  return (
    <group ref={linesRef}>
      {ringsData.map((data, i) => (
        <primitive key={i} object={data.lineObj} />
      ))}
    </group>
  )
}

function Particles({ count = 3000, speed = 1, radius = 5, color = '#ffffff', size = 0.5, rotationDirection = 1, globalOpacity = 1 }: any) {
  const pointsRef = useRef<THREE.Points>(null)
  const threeColor = useMemo(() => parseColor(color), [color])

  const [positions, scales, angles, radii, us] = useMemo(() => {
    const pos = new Float32Array(count * 3)
    const sc = new Float32Array(count)
    const ang = new Float32Array(count)
    const rad = new Float32Array(count)
    const uArr = new Float32Array(count)

    for (let i = 0; i < count; i++) {
      uArr[i] = Math.random()
      ang[i] = Math.random() * Math.PI * 2
      rad[i] = radius + (Math.random() - 0.5) * 10.0
      sc[i] = 0.5 + Math.random() * 0.5
    }
    return [pos, sc, ang, rad, uArr]
  }, [count, radius])

  const shaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: { uColor: { value: threeColor }, uSize: { value: size * 4.0 }, uGlobalOpacity: { value: globalOpacity } },
      vertexShader: `
        attribute float scale;
        attribute float uValue;
        varying float vU;
        uniform float uSize;
        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          vU = uValue;
          gl_PointSize = clamp(uSize * scale * (60.0 / max(-mvPosition.z, 1.0)), 1.0, 4.0);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 uColor; uniform float uGlobalOpacity; varying float vU;
        void main() {
          vec2 xy = gl_PointCoord.xy - vec2(0.5);
          float ll = length(xy);
          if (ll > 0.5) discard;
          float alpha = 1.0;
          if (vU < 0.03) alpha = max(0.0, vU / 0.03);
          else if (vU > 0.95) alpha = max(0.0, (1.0 - vU) / 0.05);
          alpha *= (0.5 - ll) * 3.0;
          gl_FragColor = vec4(uColor, alpha * uGlobalOpacity * 3.0);
        }
      `,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending
    })
  }, [threeColor, size, globalOpacity])

  const geometryRef = useRef<THREE.BufferGeometry>(null)
  useEffect(() => {
    if (geometryRef.current) {
      geometryRef.current.setAttribute('scale', new THREE.BufferAttribute(scales, 1))
      geometryRef.current.setAttribute('uValue', new THREE.BufferAttribute(us, 1))
    }
  }, [scales, us])

  useFrame((_, delta) => {
    if (!pointsRef.current) return
    if (typeof document !== 'undefined' && document.hidden) return
    const clampedDelta = (typeof delta === 'number' && !isNaN(delta) && isFinite(delta))
      ? Math.min(Math.max(0, delta), 0.05)
      : 0.016
    const xSweep = 30
    const posAttr = pointsRef.current.geometry.attributes.position as THREE.BufferAttribute
    const uAttr = pointsRef.current.geometry.attributes.uValue as THREE.BufferAttribute

    for (let i = 0; i < count; i++) {
      let u = (us[i]! + speed * clampedDelta * 0.03) % 1.0
      if (u < 0) u += 1.0
      us[i] = u
      uAttr.array[i] = u
      const t = Math.max(0, Math.min(0.9999, u)) // linear, clamped to prevent NaN in Math.pow

      const pos = getC(t, xSweep)

      const contraction = 15 + (radii[i]! - 15) * Math.pow(1.0 - t, 2.3)

      if (isNaN(angles[i]!)) angles[i] = Math.random() * Math.PI * 2
      angles[i] = (angles[i]! + (0.2 + Math.pow(t, 2) * 3.0) * rotationDirection * clampedDelta) % (Math.PI * 2)

      const px = Math.cos(angles[i]!) * contraction
      const pz = Math.sin(angles[i]!) * contraction

      posAttr.array[i * 3] = pos.x + px
      posAttr.array[i * 3 + 1] = pos.y
      posAttr.array[i * 3 + 2] = pos.z + pz
    }

    posAttr.needsUpdate = true
    uAttr.needsUpdate = true
  })

  return (
    <points ref={pointsRef}>
      <bufferGeometry ref={geometryRef}>
        <bufferAttribute attach="attributes-position" count={positions.length / 3} array={positions} itemSize={3} />
      </bufferGeometry>
      <primitive object={shaderMaterial} attach="material" />
    </points>
  )
}

function ColorfulParticles({ count = 3000, speed = 1, radius = 5, size = 0.5, rotationDirection = 1, globalOpacity = 1 }: any) {
  const pointsRef = useRef<THREE.Points>(null)

  const [positions, scales, angles, radii, us] = useMemo(() => {
    const pos = new Float32Array(count * 3)
    const sc = new Float32Array(count)
    const ang = new Float32Array(count)
    const rad = new Float32Array(count)
    const uArr = new Float32Array(count)

    for (let i = 0; i < count; i++) {
      uArr[i] = Math.random()
      ang[i] = Math.random() * Math.PI * 2
      rad[i] = radius + (Math.random() - 0.5) * 15.0 // slightly wider spread
      sc[i] = 0.8 + Math.random() * 0.8 // slightly larger
    }
    return [pos, sc, ang, rad, uArr]
  }, [count, radius])

  const shaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: { uSize: { value: size * 6.0 }, uGlobalOpacity: { value: globalOpacity } },
      vertexShader: `
        attribute float scale;
        attribute float uValue;
        varying float vU;
        varying float vAngle;
        uniform float uSize;
        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          vU = uValue;
          // pass angle based on position for color variation
          vAngle = atan(position.z, position.x);
          gl_PointSize = clamp(uSize * scale * (60.0 / max(-mvPosition.z, 1.0)), 1.0, 5.0);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform float uGlobalOpacity; 
        varying float vU;
        varying float vAngle;
        
        // HSL to RGB conversion
        vec3 hsl2rgb(vec3 c) {
            vec3 rgb = clamp(abs(mod(c.x*6.0+vec3(0.0,4.0,2.0),6.0)-3.0)-1.0, 0.0, 1.0);
            return c.z + c.y * (rgb-0.5)*(1.0-abs(2.0*c.z-1.0));
        }

        void main() {
          vec2 xy = gl_PointCoord.xy - vec2(0.5);
          float ll = length(xy);
          if (ll > 0.5) discard;
          
          float alpha = 1.0;
          if (vU < 0.03) alpha = max(0.0, vU / 0.03);
          else if (vU > 0.95) alpha = max(0.0, (1.0 - vU) / 0.05);
          alpha *= (0.5 - ll) * 3.0;
          
          // RGB Color combo based on depth (vU) and angle (vAngle)
          float hue = fract(vU * 1.5 + vAngle * 0.15);
          vec3 rgbColor = hsl2rgb(vec3(hue, 0.8, 0.6));
          
          gl_FragColor = vec4(rgbColor, alpha * uGlobalOpacity * 3.0);
        }
      `,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending
    })
  }, [size, globalOpacity])

  const geometryRef = useRef<THREE.BufferGeometry>(null)
  useEffect(() => {
    if (geometryRef.current) {
      geometryRef.current.setAttribute('scale', new THREE.BufferAttribute(scales, 1))
      geometryRef.current.setAttribute('uValue', new THREE.BufferAttribute(us, 1))
    }
  }, [scales, us])

  useFrame((_, delta) => {
    if (!pointsRef.current) return
    if (typeof document !== 'undefined' && document.hidden) return
    const clampedDelta = (typeof delta === 'number' && !isNaN(delta) && isFinite(delta))
      ? Math.min(Math.max(0, delta), 0.05)
      : 0.016
    const xSweep = 30
    const posAttr = pointsRef.current.geometry.attributes.position as THREE.BufferAttribute
    const uAttr = pointsRef.current.geometry.attributes.uValue as THREE.BufferAttribute

    for (let i = 0; i < count; i++) {
      let u = (us[i]! + speed * clampedDelta * 0.03) % 1.0
      if (u < 0) u += 1.0
      us[i] = u
      uAttr.array[i] = u
      const t = Math.max(0, Math.min(0.9999, u)) // linear, clamped to prevent NaN

      const pos = getC(t, xSweep)

      const contraction = 15 + (radii[i]! - 15) * Math.pow(1.0 - t, 2.3)

      if (isNaN(angles[i]!)) angles[i] = Math.random() * Math.PI * 2
      angles[i] = (angles[i]! + (0.2 + Math.pow(t, 2) * 3.0) * rotationDirection * clampedDelta) % (Math.PI * 2)

      const px = Math.cos(angles[i]!) * contraction
      const pz = Math.sin(angles[i]!) * contraction

      posAttr.array[i * 3] = pos.x + px
      posAttr.array[i * 3 + 1] = pos.y
      posAttr.array[i * 3 + 2] = pos.z + pz
    }

    posAttr.needsUpdate = true
    uAttr.needsUpdate = true
  })

  return (
    <points ref={pointsRef}>
      <bufferGeometry ref={geometryRef}>
        <bufferAttribute attach="attributes-position" count={positions.length / 3} array={positions} itemSize={3} />
      </bufferGeometry>
      <primitive object={shaderMaterial} attach="material" />
    </points>
  )
}

function CameraRig({ enableCursorInteraction, cursorInfluence }: any) {
  const { camera, pointer, size } = useThree()

  useEffect(() => {
    const aspect = size.width / size.height
    // Desktop aspect is ~1.77. FOV is 60.
    // For mobile (aspect < 1), increase FOV to fit the wide vortex.
    if (aspect < 1) {
      // Scale FOV inversely with aspect ratio to maintain horizontal coverage
      // but cap it to avoid extreme distortion.
      const newFov = Math.min(110, 60 * (1.5 / aspect))
        ; (camera as THREE.PerspectiveCamera).fov = newFov
    } else {
      ; (camera as THREE.PerspectiveCamera).fov = 60
    }
    ; (camera as THREE.PerspectiveCamera).updateProjectionMatrix()

    camera.position.set(0, 50, 165)
    camera.lookAt(0, -60, 35)
  }, [camera, size.width, size.height])

  useFrame((_, delta) => {
    if (typeof document !== 'undefined' && document.hidden) return
    if (enableCursorInteraction) {
      const clampedDelta = (typeof delta === 'number' && !isNaN(delta) && isFinite(delta))
        ? Math.min(Math.max(0, delta), 0.05)
        : 0.016
      const factor = 1 - Math.exp(-clampedDelta * 3)
      camera.position.x += (pointer.x * cursorInfluence * 30 - camera.position.x) * factor
      camera.position.y += (50 + pointer.y * cursorInfluence * 30 - camera.position.y) * factor
      camera.lookAt(0, -60, 35)
    }
  })
  return null
}

export function Vortex({
  className,
  discCount = 60,
  particleCount = 10000,
  particleColor = '#ffffff',
  discColor = 'rgba(255, 255, 255, 0.6)',
  discLineWidth = 1,
  particleSize = 1.0,
  speed = 0.02,
  rotationDirection = 1,
  opacity = 1,
  enableCursorInteraction = true,
  cursorInfluence = 0.1,
  scale = 0.85,
  radius = 115
}: VortexProps) {

  return (
    <div className={cn('absolute inset-0 overflow-hidden bg-[#090A0D]', className)}>
      <Canvas camera={{ position: [0, 50, 165], fov: 60, near: 0.1, far: 1000 }} className="block w-full h-full" dpr={[1, 2]}>
        <color attach="background" args={['#090A0D']} />
        <fog attach="fog" args={['#090A0D', 20, 350]} />
        <group position={[0, 20, 15]} scale={scale}>
          <Rings count={discCount} speed={speed * 25} radius={radius} color={discColor} opacity={parseOpacity(discColor)} lineWidth={discLineWidth} rotationDirection={rotationDirection} globalOpacity={opacity} />
          {/* Base white layer */}
          <Particles count={particleCount} speed={speed * 25} radius={radius} color={particleColor} size={particleSize} rotationDirection={rotationDirection} globalOpacity={opacity} />
          {/* New RGB dynamic color layer */}
          <ColorfulParticles count={Math.floor(particleCount * 0.3)} speed={speed * 30} radius={radius * 1.05} size={particleSize * 1.5} rotationDirection={-rotationDirection} globalOpacity={opacity} />
        </group>
        <CameraRig enableCursorInteraction={enableCursorInteraction} cursorInfluence={cursorInfluence} />
      </Canvas>
    </div>
  )
}
